"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { Timer } from "lucide-react";
import { useToast } from "@/app/_module/components/ui/use-toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/app/_module/components/ui/tooltip";
import { useTrialDialTime } from "@/app/_module/hooks/useTrialDialTime";
import { billingPath } from "@/app/_module/hooks/useDialBlocker";

const TOAST_KEY = "ic-trial-dialtime-toasted";

const fmtH = (h: number) => (h >= 10 ? `${Math.round(h)}h` : `${(Math.round(h * 10) / 10).toFixed(1)}h`);

/**
 * "14.2h of 20h left" — the trial's talk-time allowance, right on the dialer,
 * so a trial agent watches it tick down instead of discovering it at zero.
 * Grey while comfortable, amber under 20% / 2h, red at 0. One heads-up toast
 * per session at the amber and red thresholds. Tap → plans.
 */
export function TrialDialTimePill({ compact: _compact = false }: { compact?: boolean }) {
  const t = useTrialDialTime();
  const { toast } = useToast();
  const router = useRouter();
  const fired = useRef<string | null>(null);

  useEffect(() => {
    if (!t.isTrial || t.isLoading) return;
    const stage = t.exhausted ? "out" : t.almostOut ? "almost" : t.nearLimit ? "near" : null;
    if (!stage) return;
    let done: string | null = null;
    try { done = sessionStorage.getItem(TOAST_KEY); } catch { /* private mode */ }
    if (done === stage || fired.current === stage) return;
    fired.current = stage;
    try { sessionStorage.setItem(TOAST_KEY, stage); } catch { /* ignore */ }
    toast({
      title:
        stage === "out"
          ? `You've used your trial's ${t.limitHours} dialing hours`
          : `${fmtH(t.remainingHours)} of trial dialing time left`,
      description:
        stage === "out"
          ? "Calls are paused until you pick a plan. Your leads, numbers and history are all still here."
          : `Your trial includes ${t.limitHours} hours of talk time in total. Pick a plan any time to keep dialing without a cap.`,
      variant: stage === "near" ? "default" : "destructive",
      duration: 10000,
    });
  }, [t.isTrial, t.isLoading, t.exhausted, t.almostOut, t.nearLimit, t.remainingHours, t.limitHours, toast]);

  if (!t.isTrial || t.isLoading) return null;

  const tone = t.exhausted
    ? "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
    : t.nearLimit
      ? "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"
      : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100";
  const left = t.exhausted ? 0 : t.remainingHours;
  const label = t.exhausted ? "0h left" : `${fmtH(left)} left`;
  const headline = t.exhausted
    ? "No trial talk time left"
    : `${fmtH(left)} of trial talk time left`;
  const detail = `You've used ${t.usedHours.toFixed(1)} of the ${t.limitHours} hours included in your trial.${t.exhausted ? " Calls are paused." : ""} Pick a plan to remove the cap.`;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => router.push(billingPath())}
            aria-label={`${headline}. ${detail}`}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium tabular-nums transition-colors ${tone}`}
            data-testid="trial-dial-time-pill"
          >
            <Timer className="h-3 w-3" />
            {label}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[240px]">
          <p className="text-xs font-semibold">{headline}</p>
          <p className="mt-0.5 text-xs opacity-90">{detail}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
